import { useState } from "react";
import { SECTION_HEADING_CLASS } from "bizpandit-ui";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, Card, CardContent, Spinner, toast } from "bizpandit-ui";
import { ROUTES } from "@/app/constants";
import { useAuthStore } from "@/stores/authStore";
import { accountSettingsService } from "../services/accountSettings.service";
import { apiErrorMessage } from "@/api/apiError";
import ConsentPanel from "./ConsentPanel";
import { CalendarConnectionsCard } from "@/features/calendarSync/CalendarConnectionsCard";

/**
 * Portal → Profile → Account Settings.
 *
 * The page leads with **Deactivate my account** — reversible, password only,
 * and the right exit for almost everyone. Erasure is deliberately not a button
 * here: it sits in body text underneath, behind a link, and that link is the
 * only route to the erase page. The purge is immediate and irreversible, so the
 * friction is load-bearing rather than decorative.
 */
export default function ClientAccountSettingsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [password, setPassword] = useState("");

  const { data: eligibility } = useQuery({
    queryKey: ["account-erasure-eligibility"],
    queryFn: () => accountSettingsService.eligibility(),
  });

  const deactivate = useMutation({
    mutationFn: () => accountSettingsService.deactivate(password),
    onSuccess: () => {
      toast({
        title: "Account deactivated",
        description: "You have been signed out. Contact us whenever you want it reopened.",
      });
      // Their session is meaningless now, and leaving them on a portal page
      // they can no longer load would read as the app being broken.
      window.location.href = ROUTES.login;
    },
    onError: (e: unknown) => {
      toast({ title: "Could not deactivate", description: apiErrorMessage(e, "Unexpected error") });
    },
  });

  return (
    <div className="p-5 max-w-[760px]">
      <button
        type="button"
        onClick={() => navigate(ROUTES.portalProfile)}
        className="flex items-center gap-1.5 border border-linegray bg-white text-navy2 px-3 py-1.5 rounded-[10px] text-sm hover:bg-surface transition-colors mb-4"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Profile
      </button>

      <h1 className="m-0 text-[22px] font-semibold text-navy">Account Settings</h1>
      <p className="text-slate-500 text-[13px] mt-1 mb-5">
        Your sign-in details and what happens to your account.
      </p>

      <Card className="border border-linegray rounded-[14px] mb-4">
        <CardContent className="p-4">
          <h2 className={`m-0 mb-3 ${SECTION_HEADING_CLASS}`}>Sign-in details</h2>
          <dl className="grid grid-cols-[110px_1fr] gap-y-2 text-[13px] m-0">
            <dt className="text-slate-500">Email</dt>
            <dd className="m-0 text-navy2">{user?.email ?? "—"}</dd>
            <dt className="text-slate-500">Mobile</dt>
            <dd className="m-0 text-navy2">{user?.phone ?? "—"}</dd>
          </dl>
          <p className="text-slate-500 text-[12px] mt-3 mb-0">
            To change either of these, contact your consultant — they keep your
            record and your sign-in in step.
          </p>
        </CardContent>
      </Card>

      {/*
        A18. Between sign-in details and closing the account, deliberately:
        withdrawing consent is a lesser step than deactivating and a far lesser
        one than erasure, and putting it next to "Close my account" would invite
        someone to reach for the wrong door.
      */}
      <ConsentPanel />

      {/* BP-ISS-20260731-019 — the notice the consent above refers to. */}
      <Card className="border border-linegray rounded-[14px] mb-4">
        <CardContent className="p-4">
          <h2 className={`m-0 mb-1 ${SECTION_HEADING_CLASS}`}>Privacy Policy</h2>
          <p className="text-slate-500 text-[13px] m-0">
            What we hold about you, why, who we share it with, and how to exercise your rights.{" "}
            <a href="/privacy-notice.html" target="_blank" rel="noopener noreferrer" className="text-roseink font-medium underline underline-offset-2 hover:text-rosedark">
              Read the privacy notice
            </a>
          </p>
        </CardContent>
      </Card>

      <div className="mb-4"><CalendarConnectionsCard returnTo="/portal/account" /></div>

      {/*
        NO SELF-SERVICE DATA DOWNLOAD, and this is a product decision rather
        than an omission (owner, 2026-08-21): a client may VIEW their data in
        the application and may DELETE it, but may not take a machine-readable
        copy away.

        The DPDP §6 access right is still met. The privacy notice promises "a
        copy of what we hold about you" within 30 days and routes the request
        to privacy@bizpandit.com — a staff member answers it using
        GET /admin/data-rights/export/:kind/:id, which is gated on
        data_rights:export. The obligation is on BizPandit to provide a copy on
        request, not to expose a download button.

        Do not reinstate a client-facing export here without asking.
      */}

      <Card className="border border-linegray rounded-[14px]">
        <CardContent className="p-4">
          <h2 className={`m-0 mb-3 ${SECTION_HEADING_CLASS}`}>Close my account</h2>

          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <div className="bg-surface border border-linegray rounded-[10px] p-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                You keep
              </div>
              <div className="text-[13px] text-navy2">
                Your reports, charts and history, exactly as they are.
              </div>
            </div>
            <div className="bg-surface border border-linegray rounded-[10px] p-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                You lose
              </div>
              <div className="text-[13px] text-navy2">
                Access, until you ask us to reopen the account.
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="px-3 py-1.5 rounded-[10px] text-sm font-medium bg-navy text-white hover:bg-navy2 transition-colors"
          >
            Deactivate my account
          </button>

          {/*
            The only route to the erase page. Not in the navigation, not in
            search, not linked from anywhere else — see §3.1.
          */}
          <p className="text-[13px] text-slate-500 mt-4 mb-0 leading-relaxed">
            You may also request permanent erasure of your personal data. This{" "}
            <strong className="text-navy2">cannot be undone</strong>.{" "}
            <button
              type="button"
              onClick={() => navigate(ROUTES.portalEraseData)}
              className="text-rose underline underline-offset-2 hover:text-rosedark transition-colors"
            >
              Learn what this means
            </button>
          </p>

          {eligibility && !eligibility.canErase && (
            <p className="text-[12px] text-amber-800 mt-2 mb-0">
              Your account has outstanding items, so it cannot be closed right
              now. Please contact support.
            </p>
          )}
        </CardContent>
      </Card>

      {confirmOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-[14px] border border-linegray max-w-[420px] w-full p-5">
            <h3 className="m-0 text-[16px] font-semibold text-navy">
              Deactivate your account?
            </h3>
            <p className="text-[13px] text-slate-500 mt-2 mb-4 leading-relaxed">
              You will be signed out on every device. Nothing is deleted —
              contact us whenever you want it reopened.
            </p>
            <label className="block text-[12px] font-semibold text-navy2 mb-1">
              Confirm your password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-linegray rounded-[10px] px-3 py-2 text-sm mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setConfirmOpen(false); setPassword(""); }}
                className="border border-linegray bg-white text-navy2 px-3.5 py-2 rounded-[10px] text-[13px] hover:bg-surface transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!password || deactivate.isPending}
                onClick={() => deactivate.mutate()}
                className={`px-3.5 py-2 rounded-[10px] text-[13px] font-medium transition-colors
                  ${!password || deactivate.isPending
                    ? "bg-surface text-slate-400 cursor-not-allowed border border-linegray"
                    : "bg-navy text-white hover:bg-navy2"}`}
              >
                {deactivate.isPending ? <Spinner /> : "Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
